import { useResourceRoutes } from 'shared/api/resourceRouting'
import { useNavigateTo } from 'infrastructure/routing'
import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { Column } from 'shared/ui/Table/types'
import { useListPagination } from 'shared/api/list'
import { keyTest, objectTest, exportCsv } from 'shared/utils'
import { PartnerType, ProductType } from '../products/types'
import { byStringFieldSort } from '../../Helpers'
import { Button } from '@mui/material'

export function useIdNavigation(key: string) {
  const { show } = useResourceRoutes(key)
  const navigate = useNavigateTo()

  return useCallback(
    ({ id }: { id: number | string }) => {
      navigate(show(id))
    },
    [navigate, show],
  )
}

const getNodeText = (
  node: string | ReactElement | ReactNode,
): string | number => {
  if (!node) return ''
  if (typeof node === 'string' || typeof node === 'number') return node
  if (typeof node === 'object' && 'props' in node && node.props.title)
    return node.props.title
  return ''
}

function toStringsList<T>(data: T[], columns: Column<T>[]) {
  const stringConvert = (
    item: T,
    converter: (param: T) => string | ReactElement | ReactNode,
  ) => {
    return getNodeText(converter(item))
  }
  return [
    columns.map(({ title }) => getNodeText(title)),
    ...data.map((item, rowIndex) => {
      const conv = ({ rowContent }: Column<T>, colIndex: number) =>
        stringConvert(item, (p: T) => rowContent(p, { rowIndex, colIndex }))
      return columns.map(conv)
    }),
  ]
}

export function useExportData<T>(
  resourceKey: string,
  data: T[],
  columns: Column<T>[],
) {
  const pagination = useListPagination()
  return useCallback(() => {
    const start = pagination.page * pagination.rowsPerPage
    const title = `${resourceKey}_${start}-${start + pagination.rowsPerPage}`
    const stringData = toStringsList(data, columns)
    exportCsv(stringData, title)
  }, [resourceKey, data, columns, pagination])
}

export function isPartnerTest(value: unknown): value is PartnerType {
  if (!objectTest(value)) return false
  const idParam = 'id'
  if (!keyTest(value, idParam)) return false
  const idVal = value[idParam]
  return keyTest(value, 'name') && typeof idVal === 'number'
}

export function useSortableProductColumns(products: ProductType[]) {
  const [sortedField, setSortedField] = useState<keyof ProductType>('title')
  return useMemo(() => {
    const sorted = [...products].sort(byStringFieldSort(sortedField))
    return {
      sorted,
      columns: [
        {
          title: (
            <Button
              style={{ fontWeight: sortedField === 'id' ? 'bold' : '' }}
              onClick={() => setSortedField('id')}
            >
              ID
            </Button>
          ),
          width: 50,
          rowContent({ id }: ProductType) {
            return id
          },
        },
        {
          title: (
            <Button
              style={{ fontWeight: sortedField === 'title' ? 'bold' : '' }}
              onClick={() => setSortedField('title')}
            >
              Title
            </Button>
          ),
          width: 100,
          rowContent({ title }: ProductType) {
            return title
          },
        },
      ],
    }
  }, [sortedField, products])
}
